/**
 * Voice AI — Self Healing Config (Settings Sub-Tab)
 * Toggle switches for auto-fix behavior, pattern threshold, test mode.
 */
import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { settingsService } from '../../services/settings-service.js';

export class SelfHealingConfig extends LitElement {
  static properties = {
    _loading: { state: true },
    _saving: { state: true },
    _config: { state: true },
    _memory: { state: true },
    _dirty: { state: true },
  };

  static styles = [
    designSystem,
    componentStyles,
    settingsStyles,
    css`
      :host { display: block; }

      .config-grid {
        display: flex;
        flex-direction: column;
        gap: var(--space-5);
      }

      .config-card {
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-lg);
        padding: var(--space-5);
      }

      .config-card h4 {
        font-family: var(--font-display);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        color: var(--color-text-primary);
        margin: 0 0 var(--space-4) 0;
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .toggle-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) 0;
        border-bottom: 1px solid var(--color-border-secondary);
      }
      .toggle-row:last-child { border-bottom: none; }

      .toggle-info {
        flex: 1;
      }
      .toggle-label {
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-primary);
        margin: 0 0 2px 0;
      }
      .toggle-desc {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-secondary);
        margin: 0;
      }

      /* Toggle switch */
      .switch {
        position: relative;
        width: 44px;
        height: 24px;
        flex-shrink: 0;
        margin-left: var(--space-4);
      }
      .switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }
      .slider {
        position: absolute;
        cursor: pointer;
        top: 0; left: 0; right: 0; bottom: 0;
        background-color: #30363d;
        border-radius: 24px;
        transition: background-color 0.2s;
      }
      .slider:before {
        position: absolute;
        content: "";
        height: 18px;
        width: 18px;
        left: 3px;
        bottom: 3px;
        background-color: white;
        border-radius: 50%;
        transition: transform 0.2s;
      }
      .switch input:checked + .slider {
        background-color: var(--color-success);
      }
      .switch input:checked + .slider:before {
        transform: translateX(20px);
      }

      /* Threshold slider */
      .threshold-row {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) 0;
      }
      .threshold-info { flex: 1; }
      .threshold-control {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        flex-shrink: 0;
      }
      .threshold-control input[type="range"] {
        width: 120px;
        accent-color: var(--color-primary);
      }
      .threshold-value {
        font-family: var(--font-mono);
        font-size: var(--text-sm);
        font-weight: var(--font-bold);
        color: var(--color-primary);
        min-width: 24px;
        text-align: center;
      }

      /* Stats row */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: var(--space-3);
        margin-top: var(--space-4);
        padding-top: var(--space-4);
        border-top: 1px solid var(--color-border-secondary);
      }
      .stat-box {
        background: var(--color-bg-primary);
        border: 1px solid var(--color-border-secondary);
        border-radius: var(--radius-md);
        padding: var(--space-3);
        text-align: center;
      }
      .stat-value {
        font-family: var(--font-mono);
        font-size: var(--text-lg);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
      }
      .stat-label {
        font-family: var(--font-body);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
        margin-top: 2px;
      }

      .save-row {
        display: flex;
        justify-content: flex-end;
        margin-top: var(--space-4);
      }
    `
  ];

  constructor() {
    super();
    this._loading = true;
    this._saving = false;
    this._config = {};
    this._memory = null;
    this._dirty = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._loadConfig();
  }

  async _loadConfig() {
    this._loading = true;
    try {
      const data = await settingsService.loadMonitorConfig();
      if (data.success) {
        this._config = data.settings || {};
        this._memory = data.memory || null;
      }
    } catch (err) {
      console.error('[SelfHealingConfig] Load failed:', err);
    } finally {
      this._loading = false;
    }
  }

  _updateConfig(key, value) {
    this._config = { ...this._config, [key]: value };
    this._dirty = true;
  }

  async _save() {
    this._saving = true;
    try {
      const data = {
        voice_monitor_autofix_errors: this._config.voice_monitor_autofix_errors,
        voice_monitor_autofix_patterns: this._config.voice_monitor_autofix_patterns,
        voice_monitor_pattern_threshold: this._config.voice_monitor_pattern_threshold,
        voice_monitor_test_mode: this._config.voice_monitor_test_mode,
      };
      await settingsService.saveMonitorConfig(data);
      this._dirty = false;
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Monitor settings saved', type: 'success' }
      }));
    } catch (err) {
      this.dispatchEvent(new CustomEvent('show-toast', {
        bubbles: true, composed: true,
        detail: { message: 'Failed to save settings', type: 'error' }
      }));
    } finally {
      this._saving = false;
    }
  }

  render() {
    if (this._loading) {
      return html`<div class="config-card"><p style="color:var(--color-text-secondary)">Loading configuration...</p></div>`;
    }

    const c = this._config;

    return html`
      <div class="config-grid">
        <div class="config-card">
          <h4>
            <i class="fa-sharp fa-light fa-sliders"></i>
            Auto-Fix Behavior
          </h4>

          <div class="toggle-row">
            <div class="toggle-info">
              <p class="toggle-label">Auto-fix clear errors</p>
              <p class="toggle-desc">Fix config errors, API failures, and out-of-range values on first detection</p>
            </div>
            <label class="switch">
              <input type="checkbox"
                .checked=${c.voice_monitor_autofix_errors ?? true}
                @change=${e => this._updateConfig('voice_monitor_autofix_errors', e.target.checked)}
              />
              <span class="slider"></span>
            </label>
          </div>

          <div class="toggle-row">
            <div class="toggle-info">
              <p class="toggle-label">Progressive pattern fixes</p>
              <p class="toggle-desc">Fix quality issues (latency, barge-in, verbosity) after pattern repeats</p>
            </div>
            <label class="switch">
              <input type="checkbox"
                .checked=${c.voice_monitor_autofix_patterns ?? true}
                @change=${e => this._updateConfig('voice_monitor_autofix_patterns', e.target.checked)}
              />
              <span class="slider"></span>
            </label>
          </div>

          <div class="threshold-row">
            <div class="threshold-info">
              <p class="toggle-label">Pattern threshold</p>
              <p class="toggle-desc">Number of occurrences before auto-fixing quality issues</p>
            </div>
            <div class="threshold-control">
              <input type="range" min="2" max="10" step="1"
                .value=${String(c.voice_monitor_pattern_threshold ?? 3)}
                @input=${e => this._updateConfig('voice_monitor_pattern_threshold', parseInt(e.target.value))}
              />
              <span class="threshold-value">${c.voice_monitor_pattern_threshold ?? 3}x</span>
            </div>
          </div>

          <div class="toggle-row">
            <div class="toggle-info">
              <p class="toggle-label">Test Mode</p>
              <p class="toggle-desc">Ignore short calls and hangups when you're calling to test</p>
            </div>
            <label class="switch">
              <input type="checkbox"
                .checked=${c.voice_monitor_test_mode ?? false}
                @change=${e => this._updateConfig('voice_monitor_test_mode', e.target.checked)}
              />
              <span class="slider"></span>
            </label>
          </div>

          ${this._memory || c.open_issues !== undefined ? html`
            <div class="stats-row">
              <div class="stat-box">
                <div class="stat-value">${c.open_issues ?? 0}</div>
                <div class="stat-label">Open Issues</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${this._memory?.total_analyses ?? 0}</div>
                <div class="stat-label">Analyses</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${this._memory?.total_calls_analyzed ?? 0}</div>
                <div class="stat-label">Calls Analyzed</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${this._memory?.total_auto_fixes ?? 0}</div>
                <div class="stat-label">Auto-Fixes</div>
              </div>
              <div class="stat-box">
                <div class="stat-value">${c.pending_proposals ?? 0}</div>
                <div class="stat-label">Pending Proposals</div>
              </div>
            </div>
          ` : ''}
        </div>

        ${this._dirty ? html`
          <div class="save-row">
            <button class="btn btn-primary" @click=${this._save} ?disabled=${this._saving}>
              ${this._saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('self-healing-config', SelfHealingConfig);
